import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { RecipeBoxCard } from "@/components/recipes/RecipeBoxCard";
import { RecipeBoxCardList } from "@/components/recipes/RecipeBoxCardList";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ViewToggle } from "@/components/ui/view-toggle";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { usePendingRecipeBoxInvitations } from "@/hooks/use-pending-recipe-box-invitations";
import { createRecipeBox, updateRecipeBox, useRecipeBoxes } from "@/hooks/use-recipe-boxes";
import { useToast } from "@/hooks/use-toast";
import { useViewPreference } from "@/hooks/use-view-preference";
import { trackRecipeBoxCreated, trackRecipeBoxUpdated } from "@/lib/analytics-service";
import { acceptRecipeBoxInvitation, declineRecipeBoxInvitation } from "@/lib/recipe-box-invitations-service";
import { getUserRoleInBox } from "@/lib/recipe-box-permissions";
import type { RecipeBox } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { FirebaseError } from "firebase/app";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CreateRecipeBoxDialog } from "./home/_components/CreateRecipeBoxDialog";
import { EditRecipeBoxDialog } from "./home/_components/EditRecipeBoxDialog";
import { NewRecipeBoxTile } from "./home/_components/NewRecipeBoxTile";
import { PendingInvitationsSheet } from "./home/_components/PendingInvitationsSheet";
import { PendingInvitationsTrigger } from "./home/_components/PendingInvitationsTrigger";
import { recipeBoxBaseSchema, type RecipeBoxFormValues } from "./home/_lib/recipeBoxForm";

const skeletonKeys = ["one", "two", "three", "four"] as const;

export default function HomePage() {
  const breadcrumbs = useMemo(() => [{ label: "Recipe Boxes" }], []);
  usePageBreadcrumbs(breadcrumbs);

  const { user, profile, loading: authLoading } = useAuthProfile();
  const ownerUid = user?.uid ?? null;
  const { boxes, loading: boxesLoading, error } = useRecipeBoxes(ownerUid);
  const { viewPreference, setViewPreference } = useViewPreference();
  const {
    invitations: pendingInvitations,
    loading: invitationsLoading,
    error: invitationsError,
  } = usePendingRecipeBoxInvitations(user?.email ?? null);

  const [respondingToken, setRespondingToken] = useState<string | null>(null);
  const [isPendingInvitationsOpen, setIsPendingInvitationsOpen] = useState(false);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createFormError, setCreateFormError] = useState<string | null>(null);
  const [createCoverFile, setCreateCoverFile] = useState<File | null>(null);

  const [editingBox, setEditingBox] = useState<RecipeBox | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editFormError, setEditFormError] = useState<string | null>(null);
  const [editCoverFile, setEditCoverFile] = useState<File | null>(null);

  const createForm = useForm<RecipeBoxFormValues>({
    resolver: zodResolver(recipeBoxBaseSchema),
    defaultValues: {
      title: "",
      description: "",
    },
  });

  const editForm = useForm<RecipeBoxFormValues>({
    resolver: zodResolver(recipeBoxBaseSchema),
    defaultValues: {
      title: "",
      description: "",
    },
  });

  const isCreating = createForm.formState.isSubmitting;
  const isUpdating = editForm.formState.isSubmitting;
  const navigate = useNavigate();
  const { toast } = useToast();

  const resetCreateForm = () => {
    createForm.reset({ title: "", description: "" });
    setCreateCoverFile(null);
    setCreateFormError(null);
  };

  const handleCreateDialogChange = (open: boolean) => {
    setIsCreateDialogOpen(open);
    if (open) {
      return;
    }
    resetCreateForm();
  };

  const openCreateDialog = () => {
    handleCreateDialogChange(true);
  };

  const handleCreateSubmit = createForm.handleSubmit(async (values) => {
    if (!user) {
      setCreateFormError("You need to be signed in to create a recipe box.");
      return;
    }

    try {
      setCreateFormError(null);
      await createRecipeBox(user.uid, {
        title: values.title,
        description: values.description?.trim() || "",
        coverFile: createCoverFile,
      });
      trackRecipeBoxCreated();
      handleCreateDialogChange(false);
    } catch (err) {
      console.error("Failed to create recipe box", err);
    }
  });

  const openEditDialog = (box: RecipeBox) => {
    setEditingBox(box);
    setIsEditDialogOpen(true);
    setEditCoverFile(null);
    setEditFormError(null);
    editForm.reset({
      title: box.title,
      description: box.description,
    });
  };

  const closeEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingBox(null);
    setEditCoverFile(null);
    setEditFormError(null);
    editForm.reset({ title: "", description: "" });
  };

  const handleEditDialogChange = (open: boolean) => {
    if (open) {
      setIsEditDialogOpen(true);
      return;
    }
    closeEditDialog();
  };

  const handleEditSubmit = editForm.handleSubmit(async (values) => {
    if (!user || !editingBox) {
      setEditFormError("Unable to update this recipe box right now.");
      return;
    }

    try {
      setEditFormError(null);
      await updateRecipeBox(user.uid, editingBox.id, {
        title: values.title,
        description: values.description?.trim() || "",
        newCoverFile: editCoverFile,
        currentImageStoragePath: editingBox.imageStoragePath,
      });
      trackRecipeBoxUpdated();
      closeEditDialog();
    } catch (error) {
      console.error("Failed to update recipe box", error);
      setEditFormError("Could not update recipe box. Please try again.");
    }
  });

  const showEmptyState = !boxesLoading && boxes.length === 0;

  const respondToInvitation = async (token: string | undefined, accept: boolean) => {
    if (!token) {
      toast({
        title: "Invitation is missing a token",
        description: "Ask the sender to resend the invitation.",
        variant: "destructive",
      });
      return;
    }

    setRespondingToken(token);

    try {
      const result = accept
        ? await acceptRecipeBoxInvitation(token)
        : await declineRecipeBoxInvitation(token);

      if (result.action === "accepted" && "boxId" in result) {
        toast({
          title: "Invitation accepted",
          description: "Opening your new recipe box...",
        });
        setIsPendingInvitationsOpen(false);
        navigate(`/recipe-box/${result.boxId}`);
      } else {
        toast({
          title: "Invitation declined",
          description: "You can ask the sender for a new invitation anytime.",
        });
      }
    } catch (err) {
      console.error("Failed to respond to invitation", err);
      const message = mapInvitationResponseError(err);
      toast({
        title: "Could not update the invitation",
        description: message,
        variant: "destructive",
      });
    } finally {
      setRespondingToken(null);
    }
  };

  if (authLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-48" />
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(9rem, 1fr))" }}>
          {skeletonKeys.map((key) => (
            <Skeleton key={key} className="h-36 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
        <p className="font-headline text-lg">Sign in to see your recipe boxes.</p>
      </div>
    );
  }

  const currentTier = profile?.accountTier ?? "free";
  const shouldShowUpgradeNag = currentTier === "free";

  return (
    <div className="space-y-8">
      {user && shouldShowUpgradeNag ? (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Upgrade Your Account</span>
            </CardTitle>
            <CardDescription>
              {currentTier === "free"
                ? "Upgrade to Pro to remove ads and unlock more features! Use the mobile app to manage your subscription."
                : "Upgrade to Full Pro for unlimited recipes and no ads! Use the mobile app to manage your subscription."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Subscription management is available in the mobile app.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {user && pendingInvitations.length > 0 ? (
        <PendingInvitationsTrigger
          count={pendingInvitations.length}
          onClick={() => setIsPendingInvitationsOpen(true)}
        />
      ) : null}

      <div className="flex items-center gap-2">
        <h1 className="font-headline text-2xl font-semibold">My Recipe Boxes</h1>
        <div className="ml-auto flex items-center gap-2">
          <ViewToggle view={viewPreference} onViewChange={setViewPreference} />
          <CreateRecipeBoxDialog
            isOpen={isCreateDialogOpen}
            onOpenChange={handleCreateDialogChange}
            form={createForm}
            isSubmitting={isCreating}
            errorMessage={createFormError}
            onSubmit={handleCreateSubmit}
            onFileChange={setCreateCoverFile}
            selectedFileName={createCoverFile?.name}
          />
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p>We couldn&apos;t load your recipe boxes right now. Please try again shortly.</p>
        </div>
      ) : null}

      {boxesLoading ? (
        viewPreference === "card" ? (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(9rem, 1fr))" }}>
            {skeletonKeys.map((key) => (
              <Skeleton key={key} className="h-36 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {skeletonKeys.map((key) => (
              <Skeleton key={key} className="h-24 w-full" />
            ))}
          </div>
        )
      ) : viewPreference === "card" ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(9rem, 1fr))" }}>
          <NewRecipeBoxTile onClick={openCreateDialog} disabled={isCreating} />
          {boxes.map((box) => (
            <RecipeBoxCard 
              key={box.id} 
              recipeBox={box} 
              onEdit={getUserRoleInBox(box, user?.uid) === "owner" ? openEditDialog : undefined}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <NewRecipeBoxTile onClick={openCreateDialog} disabled={isCreating} />
          {boxes.map((box) => (
            <RecipeBoxCardList 
              key={box.id} 
              recipeBox={box} 
              onEdit={getUserRoleInBox(box, user?.uid) === "owner" ? openEditDialog : undefined}
            />
          ))}
        </div>
      )}

      {showEmptyState ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="font-headline text-lg">No recipe boxes yet.</p>
          <p className="mt-2 text-sm text-muted-foreground">Time to get cookin&apos;—add your first recipe box!</p>
        </div>
      ) : null}

      <EditRecipeBoxDialog
        isOpen={isEditDialogOpen}
        onOpenChange={handleEditDialogChange}
        form={editForm}
        isSubmitting={isUpdating}
        errorMessage={editFormError}
        onSubmit={handleEditSubmit}
        onFileChange={setEditCoverFile}
        recipeBox={editingBox}
        onCancel={closeEditDialog}
        selectedFileName={editCoverFile?.name}
        onCropSave={async (focalPointX, focalPointY, zoom) => {
          if (!user || !editingBox) return;
          try {
            await updateRecipeBox(user.uid, editingBox.id, {
              imageFocalPointX: focalPointX,
              imageFocalPointY: focalPointY,
              imageZoom: zoom,
            });
            toast({
              title: "Image position updated",
              description: "Your crop has been saved.",
            });
          } catch (err) {
            console.error("Failed to save crop", err);
            toast({
              variant: "destructive",
              title: "Could not save position",
              description: "Please try again.",
            });
          }
        }}
      />

      {user ? (
        <PendingInvitationsSheet
          invitations={pendingInvitations}
          loading={invitationsLoading}
          errorMessage={invitationsError}
          respondingToken={respondingToken}
          isOpen={isPendingInvitationsOpen}
          onOpenChange={setIsPendingInvitationsOpen}
          onAccept={(token) => respondToInvitation(token, true)}
          onDecline={(token) => respondToInvitation(token, false)}
        />
      ) : null}
    </div>
  );
}

const mapInvitationResponseError = (error: unknown): string => {
  if (error instanceof FirebaseError) {
    if (error.code === "functions/permission-denied") {
      return "Sign in with the email address that received the invitation.";
    }

    if (error.code === "functions/failed-precondition") {
      return "This invitation is no longer valid.";
    }

    if (error.code === "functions/not-found") {
      return "We couldn't find this invitation.";
    }
  }

  return "Please try again later.";
};

